import React from 'react';

interface HighlightedSegment {
  text: string;
  start: number;
  end: number;
  severity: number;
}

interface HighlightedTextProps {
  originalText: string;
  segments: HighlightedSegment[];
}

interface ProcessedSegment {
  text: string;
  isHighlighted: boolean;
  severity?: number;
}

const HighlightedText: React.FC<HighlightedTextProps> = ({ originalText, segments }) => {
  /**
   * Get background and text color based on severity level
   */
  const getSeverityColors = (severity: number): { bg: string; text: string; border: string } => {
    if (severity >= 8) {
      return { bg: 'bg-red-200', text: 'text-red-900', border: 'border-red-400' };
    } else if (severity >= 6) {
      return { bg: 'bg-orange-200', text: 'text-orange-900', border: 'border-orange-400' };
    } else if (severity >= 4) {
      return { bg: 'bg-yellow-200', text: 'text-yellow-900', border: 'border-yellow-400' };
    } else {
      return { bg: 'bg-green-200', text: 'text-green-900', border: 'border-green-400' };
    }
  };

  /**
   * Process highlighted segments and handle overlaps
   * Algorithm:
   * 1. Sort segments by start position
   * 2. Merge overlapping segments, keeping highest severity
   * 3. Create array of text chunks (highlighted and non-highlighted)
   */
  const processSegments = (): ProcessedSegment[] => {
    if (!segments || segments.length === 0) {
      return [{ text: originalText, isHighlighted: false }];
    }

    // Sort segments by start position
    const sortedSegments = [...segments].sort((a, b) => a.start - b.start);

    // Merge overlapping segments
    const mergedSegments: Array<{ start: number; end: number; severity: number }> = [];

    for (const segment of sortedSegments) {
      if (mergedSegments.length === 0) {
        mergedSegments.push({
          start: segment.start,
          end: segment.end,
          severity: segment.severity,
        });
      } else {
        const last = mergedSegments[mergedSegments.length - 1];

        // Check if segments overlap
        if (segment.start <= last.end) {
          // Overlapping - merge and keep highest severity
          last.end = Math.max(last.end, segment.end);
          last.severity = Math.max(last.severity, segment.severity);
        } else {
          // No overlap - add as new segment
          mergedSegments.push({
            start: segment.start,
            end: segment.end,
            severity: segment.severity,
          });
        }
      }
    }

    // Build processedSegments array with highlighted and non-highlighted chunks
    const result: ProcessedSegment[] = [];
    let currentPos = 0;

    for (const segment of mergedSegments) {
      // Add non-highlighted text before this segment
      if (currentPos < segment.start) {
        result.push({
          text: originalText.substring(currentPos, segment.start),
          isHighlighted: false,
        });
      }

      // Add highlighted segment
      result.push({
        text: originalText.substring(segment.start, segment.end),
        isHighlighted: true,
        severity: segment.severity,
      });

      currentPos = segment.end;
    }

    // Add remaining non-highlighted text
    if (currentPos < originalText.length) {
      result.push({
        text: originalText.substring(currentPos),
        isHighlighted: false,
      });
    }

    return result;
  };

  const processedSegments = processSegments();

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">
        Analyzed Prompt
      </h3>
      <div className="prose max-w-none">
        <div className="text-gray-800 leading-relaxed whitespace-pre-wrap font-mono text-sm">
          {processedSegments.map((segment, index) => {
            if (segment.isHighlighted && segment.severity !== undefined) {
              const colors = getSeverityColors(segment.severity);
              return (
                <span
                  key={index}
                  className={`${colors.bg} ${colors.text} ${colors.border} border-b-2 px-1 py-0.5 rounded font-semibold`}
                  title={`Manipulation detected (Severity: ${segment.severity}/10)`}
                >
                  {segment.text}
                </span>
              );
            } else {
              return <span key={index}>{segment.text}</span>;
            }
          })}
        </div>
      </div>

      {/* Legend */}
      {segments && segments.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Severity Legend:</h4>
          <div className="flex flex-wrap gap-3 text-xs">
            <div className="flex items-center gap-1">
              <span className="inline-block w-4 h-4 bg-red-200 border border-red-400 rounded"></span>
              <span className="text-gray-600">Critical (8-10)</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="inline-block w-4 h-4 bg-orange-200 border border-orange-400 rounded"></span>
              <span className="text-gray-600">High (6-7)</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="inline-block w-4 h-4 bg-yellow-200 border border-yellow-400 rounded"></span>
              <span className="text-gray-600">Medium (4-5)</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="inline-block w-4 h-4 bg-green-200 border border-green-400 rounded"></span>
              <span className="text-gray-600">Low (1-3)</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HighlightedText;
